import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import BASE_URL from "../config";
import {
  CaciStyles, PageHeader, Pager, ArticleCard, SkelGrid, SectionNote,
  SearchField, PerPageSelect, usePagedList,
  useReveal, useScrollProgress,
  excerptOf, truncate, langPath, localized, localizedImage,
} from "../components/CaciUI";

/* ═══════════════════════════════════════════════════════════════════════════
   TOUTPAGE — the page directory
   ───────────────────────────────────────────────────────────────────────────
   Same endpoint (/api/pages), same route to /page/:id/:slug. The listing
   mechanics — URL-synced filters, debounce, cancellation, page reset, the
   dim-don't-blank refetch — now come from the shared usePagedList hook, so
   this file only describes what a page result looks like.

   Fixes carried in with the hook:
   · Filters live in the URL, so a filtered view survives a refresh and the
     Back button behaves.
   · Real disabled pagination buttons. The old markup put `disabled` on <a>
     tags, which does nothing, so "previous" fired a request for page 0.
   · The list dims instead of being replaced by a spinner on every keystroke.
   · A failed request is now its own state with a retry, rather than showing
     "no results" and implying the directory is empty.
   · A missing slug produced "/page/12/undefined"; the optional segment is
     simply left off.
   ═══════════════════════════════════════════════════════════════════════════ */

function Toutpage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isRtl = lang === "ar";
  const inputRef = useRef(null);

  const list = usePagedList({
    url: `${BASE_URL}/api/pages`,
    perPageOptions: [6, 12, 24, 48],
    defaultPerPage: 12,
  });

  const pageRef = useReveal([list.items, list.state]);
  const progress = useScrollProgress();

  const clearSearch = () => {
    list.setTerm("");
    inputRef.current && inputRef.current.focus();
  };

  const linkFor = (p) => (p.slug ? `/page/${p.id}/${p.slug}` : `/page/${p.id}`);

  const pageTitle = t("menu.Toutpage", t("menu.toutpage", "Pages"));
  const nf = lang === "ar" ? "ar-DZ" : lang === "en" ? "en-US" : "fr-FR";

  return (
    <div className={`caci-page${isRtl ? " is-rtl" : ""}`} ref={pageRef} dir={isRtl ? "rtl" : "ltr"}>
      <CaciStyles />
      <style>{PAGE_CSS}</style>

      <Helmet>
        <title>{pageTitle} — {t("menu.caci")}</title>
        <meta name="description" content={t("home.Toutpage", pageTitle)} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={t("home.Toutpage", pageTitle)} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={typeof window !== "undefined" ? window.location.href : ""} />
      </Helmet>

      <div
        className="caci-progress"
        style={{ transform: `scaleX(${progress})`, opacity: progress > 0.005 ? 1 : 0 }}
        aria-hidden="true"
      />

      <PageHeader
        title={pageTitle}
        crumbs={[{ label: t("menu.home"), to: langPath(lang, "/") }, { label: pageTitle }]}
        count={
          list.state === "ready" && list.total > 0
            ? { value: list.total.toLocaleString(nf), label: t("search.results", "résultats") }
            : null
        }
      />

      <div className="bar">
        <div className="caci-shell">
          <div className="bar-inner">
            <SearchField
              inputRef={inputRef}
              value={list.term}
              onChange={list.setTerm}
              onClear={clearSearch}
              placeholder={t("search.placeholder")}
              busy={list.refetching}
              clearLabel={t("home.Close")}
            />
            <div className="bar-filters">
              <PerPageSelect
                value={list.perPage}
                options={list.perPageOptions}
                onChange={(v) => list.patch({ per: v })}
                label={t("search.perPage")}
              />
            </div>
          </div>
        </div>
      </div>

      <section className="caci-section" ref={list.listRef}>
        <div className="caci-shell">
          {list.state === "loading" && <SkelGrid count={Math.min(list.perPage, 12)} />}

          {list.state === "error" && (
            <SectionNote
              state="error"
              title={t("search.errorTitle", "Chargement impossible")}
              text={t("home.section_error", "Cette section n'a pas pu être chargée.")}
              action={
                <button type="button" className="btn-quiet" onClick={list.reload}>
                  <i className="bi bi-arrow-clockwise" />
                  {t("home.retry", "Réessayer")}
                </button>
              }
            />
          )}

          {list.state === "ready" && list.items.length === 0 && (
            <SectionNote
              title={t("search.noResults")}
              text={t("search.tryDifferentTerms")}
              action={
                list.isFiltered ? (
                  <button type="button" className="btn-quiet" onClick={list.clearAll}>
                    <i className="bi bi-arrow-counterclockwise" />
                    {t("search.clear", "Tout effacer")}
                  </button>
                ) : null
              }
            />
          )}

          {list.state === "ready" && list.items.length > 0 && (
            <>
              <div className={`caci-grid${list.refetching ? " is-busy" : ""}`}>
                {list.items.map((p, i) => (
                  <ArticleCard
                    key={p.id ?? i}
                    index={i}
                    to={linkFor(p)}
                    image={localizedImage(p, "image", lang, BASE_URL)}
                    title={truncate(localized(p, "name", lang), 78)}
                    excerpt={excerptOf(localized(p, "description", lang), 20)}
                    readLabel={t("home.Read_More")}
                    isRtl={isRtl}
                  />
                ))}
              </div>

              <div className="pager-wrap">
                <Pager
                  page={list.page}
                  totalPages={list.totalPages}
                  onChange={list.goToPage}
                  labels={{
                    first: t("search.first", "Première page"),
                    prev: t("search.prev", "Page précédente"),
                    next: t("search.next", "Page suivante"),
                    last: t("search.last", "Dernière page"),
                    page: t("search.page", "Page"),
                    of: t("search.of", "sur"),
                  }}
                />
              </div>
            </>
          )}

          <p className="caci-sr" aria-live="polite">
            {list.state === "ready" ? `${list.total} ${t("search.results", "résultats")}` : ""}
          </p>
        </div>
      </section>
    </div>
  );
}

const PAGE_CSS = `
.caci-page .caci-grid.is-busy { opacity: .45; pointer-events: none; transition: opacity .2s ease; }
.caci-page .pager-wrap { margin-block-start: clamp(36px, 6vw, 64px); }
`;

export default Toutpage;
